import { useState, useEffect } from "react";
import axios from "axios";

import { useAtom } from "jotai";
import { gbState } from "../../../../components/shared/GlobalState";

const FinancialDataComponent = () => {
  const [incomesData, setIncomesData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [maxScale, setMaxScale] = useState(2000);
  //Error Logging Service
  const [{ errorLogApi, user }] = useAtom(gbState);

  const [financial_year] = useState({
    value: user?.current_financial_year,
    label: user?.current_financial_year,
  });

  const [hoveredData, setHoveredData] = useState(null);

  const getCurrentMonths = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentMonth = new Date().getMonth();
    // Get last 6 months including current month
    const startMonth = currentMonth - 5;

    // If we're in the early months of the year, we need to wrap around to previous year's months
    if (startMonth < 0) {
      // Combine end of previous year and start of current year
      return [
        ...months.slice(startMonth + 12),
        ...months.slice(0, currentMonth + 1),
      ];
    }

    // Otherwise just return last 6 months
    return months.slice(startMonth, currentMonth + 1);
  };

  useEffect(() => {
    const getIncomeDataFromServer = async () => {
      const userToken = localStorage.getItem("token");
      const urlLink = `${process.env.REACT_APP_API_URL}/income/${user?.church_id}`;

      try {
        const response = await axios.get(urlLink, {
          headers: { Authorization: `Bearer ${userToken}` },
        });

        const oldData = response?.data?.incomes?.filter(
          (finFilter) => finFilter?.financial_year === financial_year?.label
        );
        const updatedData = oldData.map((data) => ({
          ...data,
          id: data?._id,
          category: data?.category_id?.category_name,
        }));

        setIncomesData(updatedData);
        setIsLoading(false);
      } catch (err) {
        console.log("error api call ", err);
        setIsLoading(false);
      }
    };
    const getExpenseDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/expense/${user.church_id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };
      axios(config)
        .then(function (response) {
          const oldData = response.data.expenses?.filter(
            (finFilter) => finFilter?.financial_year === financial_year?.label
          );
          const updatedData = oldData.map((data) => {
            return {
              ...data,
              id: data?._id,
              category: data?.category_id?.category_name,
            };
          });

          setExpenseData(updatedData);
          setIsLoading(false);
        })
        .catch(function (err) {
          console.log("error api call ", err);
          setIsLoading(false);
        });
    };

    getIncomeDataFromServer();
    getExpenseDataFromServer();
  }, [financial_year]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (incomesData.length === 0 && expenseData.length === 0) {
    return (
      <div className="w-full h-[380px] flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p className="mb-2 text-lg">No financial data available</p>
          <p className="text-sm">
            Start adding collections and expenses to see your financial overview
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full h-[380px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            Monthly Collections and Expenses
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Scale: ${(maxScale/1000).toFixed(1)}k</span>
            <input
              type="range"
              min="1000"
              max="10000"
              step="1000"
              value={maxScale}
              onChange={(e) => setMaxScale(Number(e.target.value))}
              className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
        <div className="relative w-full h-[250px] px-12">
          <div className="flex gap-1 h-full">
            {getCurrentMonths().map((month, monthIndex) => {
              const currentMonth = new Date().getMonth();
              const startMonth = currentMonth - 5;
              const adjustedIndex = startMonth < 0 
                ? (monthIndex < (6 + startMonth) ? monthIndex + 12 + startMonth : monthIndex + startMonth)
                : monthIndex + startMonth;

              const monthIncomes = incomesData.filter((income) => {
                const incomeMonth = new Date(income.date).getMonth();
                return incomeMonth === adjustedIndex;
              });
              const monthExpenses = expenseData.filter((expense) => {
                const expenseMonth = new Date(expense.date).getMonth();
                return expenseMonth === adjustedIndex;
              });

              const totalIncome = monthIncomes.reduce(
                (sum, income) => sum + income.amount,
                0
              );
              const totalExpense = monthExpenses.reduce(
                (sum, expense) => sum + expense.amount,
                0
              );

              const maxValue = Math.max(
                ...incomesData.map((income) => income.amount),
                ...expenseData.map((expense) => expense.amount),
                maxScale
              );

              const incomeHeight = (totalIncome / maxValue) * 80;
              const expenseHeight = (totalExpense / maxValue) * 80;

              return (
                <div
                  key={month}
                  className="flex flex-1 gap-1 justify-center items-end pb-12"
                >
                  <div
                    className="relative w-4 bg-blue-400 rounded-t transition-all duration-300 cursor-pointer hover:bg-blue-500"
                    style={{ height: `${incomeHeight}%` }}
                    onMouseEnter={() =>
                      setHoveredData({
                        month,
                        type: "income",
                        amount: totalIncome,
                      })
                    }
                    onMouseLeave={() => setHoveredData(null)}
                  />
                  <div
                    className="relative w-4 bg-red-400 rounded-t transition-all duration-300 cursor-pointer hover:bg-red-500"
                    style={{ height: `${expenseHeight}%` }}
                    onMouseEnter={() =>
                      setHoveredData({
                        month,
                        type: "expense",
                        amount: totalExpense,
                      })
                    }
                    onMouseLeave={() => setHoveredData(null)}
                  />
                  <div className="absolute bottom-2 text-xs transform origin-top-left -rotate-45 translate-y-2">
                    {month}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="absolute top-0 left-0 flex flex-col justify-between h-[85%]">
            {[...Array(6)].map((_, i) => {
              const value = (maxScale / 5) * (5 - i);
              return (
                <div key={i} className="text-xs text-gray-500 -translate-x-2">
                  ${(value/1000).toFixed(1)}k
                </div>
              );
            })}
          </div>

          {hoveredData && (
            <div className="absolute z-10 p-2 text-sm text-white rounded shadow-lg transform -translate-x-1/2 -translate-y-full bg-gray-800/90">
              <div className="font-semibold">{hoveredData.month}</div>
              <div>
                {hoveredData.type === "income" ? "Collection" : "Expense"}
              </div>
              <div>${hoveredData.amount.toLocaleString()}</div>
            </div>
          )}
        </div>
        <div className="flex gap-8 justify-center mt-2">
          <div className="flex gap-2 items-center">
            <div className="w-3 h-3 bg-blue-400" />
            <span className="text-sm">Collections</span>
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-3 h-3 bg-red-400" />
            <span className="text-sm">Expenses</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDataComponent;
